import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { api, endpoints } from '../config/api';
import { AiSettings } from '../types';

const AiAssistantSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<AiSettings | null>(null);
  const [formData, setFormData] = useState({
    aiAssistantInstructions: '',
    geminiModel: 'gemini-2.5-flash',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    void fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get(endpoints.aiSettings);
      const data = (response.data?.data || response.data) as AiSettings;
      setSettings(data);
      setFormData({
        aiAssistantInstructions: data?.aiAssistantInstructions || '',
        geminiModel: data?.geminiModel || 'gemini-2.5-flash',
      });
    } catch (err) {
      console.error('AI settings load error:', err);
      setError('Impossibile caricare le impostazioni AI');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await api.put(endpoints.aiSettings, formData);
      const data = (response.data?.data || response.data) as AiSettings;
      setSettings(data);
      setSuccess('Impostazioni AI salvate con successo');
    } catch (err) {
      console.error('AI settings save error:', err);
      setError('Impossibile salvare le impostazioni AI');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="320px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Assistente AI
          </Typography>
          <Typography color="text.secondary">
            Queste istruzioni vengono riutilizzate da tutte le tappe che hanno la modalita AI attiva.
          </Typography>
        </Box>
        <Chip
          icon={<SmartToyIcon />}
          color={settings?.geminiConfigured ? 'success' : 'warning'}
          label={settings?.geminiConfigured ? 'Gemini configurato' : 'Gemini non configurato'}
          sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
        />
      </Stack>

      <Card>
        <CardContent>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Modello Gemini"
              value={formData.geminiModel}
              onChange={(e) => setFormData((current) => ({ ...current, geminiModel: e.target.value }))}
              helperText="Default consigliato: gemini-2.5-flash"
            />
            <TextField
              fullWidth
              multiline
              minRows={10}
              label="Istruzioni globali assistente"
              value={formData.aiAssistantInstructions}
              onChange={(e) => setFormData((current) => ({ ...current, aiAssistantInstructions: e.target.value }))}
              helperText="Inserisci tono, limiti, stile di risposta e comportamento del personaggio AI."
            />
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Salvataggio...' : 'Salva impostazioni AI'}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')}>
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AiAssistantSettingsPage;
